import { RolodexEntryJson, Severity } from 'types'
import { RolodexEntryType } from 'types/rolodex/rolodexEntries'
import { parseString } from 'utils/parsingHelpers'

import { GeckoEntryBase } from './geckoEntry'

export enum TicketStatus {
  OPEN = 'OPEN',
  IN_PROGRESS = 'IN PROGRESS',
  DONE = 'DONE',
  NEW = 'NEW',
  ACKED = 'ACKED',
}

/**
 * An entry that holds a processed point cloud the and associated
 * metadata.
 */
export class GeckoObservationTicket extends GeckoEntryBase<
  {
    id: string
    status: TicketStatus
    name?: string
    type?: string
    area_id?: string
    severity?: Severity
    component?: string
    condition?: string
    equipment?: string
    wo_number?: string
    date_found?: number | string
    description?: string
    notification_number?: string
    original_created_date?: number | string
    [key: string]: unknown
  },
  {
    site: string
    organization: string
    [RolodexEntryType.POTREE_POINT_CLOUD]?: string
    [RolodexEntryType.OBSERVATION]: string
  }
> {
  //----------------------------------------------------------------------------

  /**
   * A simple constructor, private so that a more descriptive factory method
   * (which can return undefined) is used instead
   * @param json The raw JSON to create an instance from
   * @private
   */
  private constructor(json: RolodexEntryJson) {
    super(
      json,
      {
        id: json.id,
        status:
          (parseString(json.data.status) as TicketStatus) ?? TicketStatus.NEW,
        name: parseString(json.data.name),
        type: parseString(json.data.type),
        area_id: parseString(json.data.area_id),
        severity: parseString(json.data.severity) as Severity | undefined,
        component: parseString(json.data.component),
        condition: parseString(json.data.condition),
        equipment: parseString(json.data.equipment),
        wo_number: parseString(json.data.wo_number),
        date_found:
          typeof json.data.date_found === 'string' ||
          typeof json.data.date_found === 'number'
            ? json.data.date_found
            : undefined,
        description: parseString(json.data.description),
        notification_number: parseString(json.data.notification_number),
        original_created_date:
          typeof json.data.original_created_date === 'string' ||
          typeof json.data.original_created_date === 'number'
            ? json.data.original_created_date
            : undefined,
      },
      {
        organization: parseString(json.tags.organization) ?? '',
        site: parseString(json.tags.site) ?? '',
        [RolodexEntryType.POTREE_POINT_CLOUD]: parseString(
          json.tags[RolodexEntryType.POTREE_POINT_CLOUD]
        ),
        [RolodexEntryType.OBSERVATION]:
          parseString(json.tags[RolodexEntryType.OBSERVATION]) ?? '',
      },
      {}
    )
  }

  //----------------------------------------------------------------------------

  /**
   * A simple factory method which creates a ticket from raw JSON
   * @param json The raw JSON to create an instance from
   * @constructor
   */
  public static CreateFromJSON(json: RolodexEntryJson) {
    return new GeckoObservationTicket(json)
  }
}
